<script setup>
import DescriptionInline from "./DescriptionInline.vue";
import Header from "./Header.vue";
import ButtonSubmit from "./ButtonSubmit.vue";
import ButtonCancel from "./ButtonCancel.vue";
import api from "../api/api";
import Multiselect from "@vueform/multiselect";
import { flashMessage } from "../functions.js";
</script>
<template>
  <section>
    <!-- Manage sub-organization Section -->
    <div class="subOrganizationBox">
      <Header label="Manage sub-organizations" />
      <div class="formBox">
        <div>
          <label for="subOrganization"
            ><DescriptionInline label="Sub-organization*"
          /></label>
          <input
            v-model="subOrganizationInput"
            id="subOrganization"
            class="formControl"
            required
          />
        </div>

        <div class="buttonBox">
          <ButtonSubmit
            label="Add"
            :disabled="!subOrganizationInput"
            @click.prevent="addSubOrg()"
          />
          <ButtonCancel
            label="Cancel"
            :disabled="!subOrganizationInput"
            @click.prevent="clearAddField()"
          />
        </div>
      </div>

      <div class="formBox">
        <div>
          <div class="textOnInput">
            <label for="selectSubOrganization"
              ><DescriptionInline label="Select sub-organization*"
            /></label>
            <select
              class="formControl"
              id="selectSubOrganization"
              name="subOrganization"
              v-model="chosenSubOrg"
              @change="selectSubOrg($event)"
              required
            >
              <option disabled>Select sub-organization</option>
              <option
                v-for="subOrganization in sortSubOrganizationList"
                :value="subOrganization.suborg_name"
                :key="subOrganization.suborg_id"
              >
                {{ subOrganization.suborg_name }}
              </option>
            </select>
          </div>
          <div class="textOnInput">
            <label for="subOrganization"
              ><DescriptionInline label="Sub-organization*"
            /></label>
            <input
              v-model.lazy="chosenSubOrg"
              id="subOrganization"
              class="formControl"
              required
              :disabled="!chosenSubOrg"
            />
          </div>
          <label for="surveyTemplate" class="form-label"
            ><DescriptionInline label="Update survey template"
          /></label>
          <Multiselect
            class="formControl"
            v-model="chosenSurveyTemplate"
            @change="selectSurvey($event)"
            mode="tags"
            :close-on-select="false"
            :searchable="true"
            :options="sortSurveyTemaplateList"
            :disabled="!chosenSubOrg"
          />
        </div>

        <div class="buttonBox">
          <ButtonSubmit
            label="Update"
            :disabled="!chosenSubOrg"
            @click="updateSubOrg()"
          />
          <ButtonCancel
            label="Cancel"
            :disabled="!chosenSubOrg"
            @click="clearUpdatedFields()"
          />
        </div>

        <!-- Multiple Language Selection Section -->
        <div class="formBox">
          <h4 class="profile-header">Manage available languages for sub-organization</h4>
          <hr />
          <label for="multi-language-select" class="main-label">Select Available Languages:</label>
          <Multiselect
            v-model="selectedLanguages"
            mode="tags"
            :close-on-select="false"
            :searchable="true"
            :options="availableLanguages"
            label="label"
            track-by="label"
            :disabled="!chosenSubOrg"
            class="formControl"
            @update:model-value="saveLanguages"
            :filter-results="true"
            :resolve-on-load="false"
            :min-chars="1"
          />
        </div>
        
        <!-- Language Selection Section -->
        <div class="formBox">
          <h4 class="profile-header">Manage language preference for sub-organization</h4>
          <hr />
          <label for="language-select" class="main-label">Select Language:</label>
          <select 
            id="language-select"
            v-model="selectedLanguage" 
            @change="saveLanguage"
            :disabled="!chosenSubOrg || selectedLanguages.length === 0"
            class="language-select"
          >
            <option disabled value="">Select from available languages</option>
            <option 
              v-for="lang in filteredAvailableLanguages" 
              :key="lang.value" 
              :value="lang.value"
            >
              {{ lang.label }}
            </option>
          </select>
        </div>

        <div class="formBox">
          <div class="tooltip">
            Enable translation at Iteration level
            <span class="tooltiptext">
              Enable this if you want each Iteration to have its own translated content.
              Default is No (translations are managed at sub-organization level).
            </span>
          </div>
          <select 
            class="formControl" 
            id="defineTranslationPerIteration" 
            :disabled="!chosenSubOrg" 
            v-model="enableTranslationAtIterationLevel"
            @change="saveTranslationSetting"
          >
            <option value="1">Yes</option>
            <option selected value="0">No</option>
          </select>
        </div>
        

      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "ManageSubOrgBox",
  component: [
    DescriptionInline,
    Header,
    ButtonSubmit,
    ButtonCancel,
    Multiselect,
  ],
  props: ["userData", "brandData"],

  data: () => ({
    subOrgList: [],
    subOrganizationInput: "",
    subOrgId: "",
    chosenSubOrg: "",
    chosenSurveyTemplate: [],
    surveyTemplateList: [],
    surveyURL: "",
    fullSurveyTemplateList: [],
    surveyFilenameId: [],
    orgName: "",
    addClick: null,
    selectedLanguage: "",
    selectedLanguages: ["en"], // Array to store multiple selected languages
      availableLanguages: [
        { value: "af", label: "Afrikaans" },
        { value: "ak", label: "Akan" },
        { value: "sq", label: "Albanian" },
        { value: "am", label: "Amharic" },
        { value: "ar", label: "Arabic" },
        { value: "hy", label: "Armenian" },
        { value: "as", label: "Assamese" },
        { value: "ay", label: "Aymara" },
        { value: "az", label: "Azerbaijani" },
        { value: "bm", label: "Bambara" },
        { value: "ba", label: "Bashkir" },
        { value: "eu", label: "Basque" },
        { value: "be", label: "Belarusian" },
        { value: "bn", label: "Bengali" },
        { value: "bs", label: "Bosnian" },
        { value: "pt-br", label: "Brazilian Portuguese" },
        { value: "bg", label: "Bulgarian" },
        { value: "my", label: "Burmese" },
        { value: "yue", label: "Cantonese" },
        { value: "ca", label: "Catalan" },
        { value: "ceb", label: "Cebuano" },
        { value: "km", label: "Central Khmer" },
        { value: "ny", label: "Chichewa" },
        { value: "chk", label: "Chuukese" },
        { value: "co", label: "Corsican" },
        { value: "hr", label: "Croatian" },
        { value: "cs", label: "Czech" },
        { value: "da", label: "Danish" },
        { value: "dv", label: "Divehi" },
        { value: "nl", label: "Dutch" },
        { value: "en", label: "English" },
        { value: "eo", label: "Esperanto" },
        { value: "et", label: "Estonian" },
        { value: "ee", label: "Ewe" },
        { value: "fo", label: "Faroese" },
        { value: "fj", label: "Fijian" },
        { value: "fl", label: "Filipino" },
        { value: "fi", label: "Finnish" },
        { value: "fr", label: "French" },
        { value: "fr-ca", label: "French (Canada)" },
        { value: "gl", label: "Galician" },
        { value: "ka", label: "Georgian" },
        { value: "de", label: "German" },
        { value: "el", label: "Greek" },
        { value: "gn", label: "Guarani" },
        { value: "gu", label: "Gujarati" },
        { value: "ht", label: "Haitian Creole" },
        { value: "ha", label: "Hausa" },
        { value: "hw", label: "Hawaiian" },
        { value: "he", label: "Hebrew" },
        { value: "hi", label: "Hindi" },
        { value: "hmn", label: "Hmong" },
        { value: "hu", label: "Hungarian" },
        { value: "is", label: "Icelandic" },
        { value: "ig", label: "Igbo" },
        { value: "il", label: "Ilocano" },
        { value: "id", label: "Indonesian" },
        { value: "iu", label: "Inuktitut" },
        { value: "ga", label: "Irish" },
        { value: "it", label: "Italian" },
        { value: "ja", label: "Japanese" },
        { value: "jv", label: "Javanese" },
        { value: "kn", label: "Kannada" },
        { value: "kk", label: "Kazakh" },
        { value: "rw", label: "Kinyarwanda" },
        { value: "ko", label: "Korean" },
        { value: "ku", label: "Kurdish" },
        { value: "kb", label: "Kurdish (Sorani)" },
        { value: "ky", label: "Kyrgyz" },
        { value: "lo", label: "Lao" },
        { value: "la", label: "Latin" },
        { value: "lv", label: "Latvian" },
        { value: "ln", label: "Lingala" },
        { value: "lt", label: "Lithuanian" },
        { value: "lg", label: "Luganda" },
        { value: "lb", label: "Luxembourgish" },
        { value: "mk", label: "Macedonian" },
        { value: "mg", label: "Malagasy" },
        { value: "ms", label: "Malay" },
        { value: "ml", label: "Malayalam" },
        { value: "mt", label: "Maltese" },
        { value: "mr", label: "Marathi" },
        { value: "mh", label: "Marshallese" },
        { value: "mn", label: "Mongolian" },
        { value: "mi", label: "Māori" },
        { value: "ne", label: "Nepali" },
        { value: "no", label: "Norwegian" },
        { value: "nb", label: "Norwegian Bokmål" },
        { value: "oc", label: "Occitan" },
        { value: "or", label: "Odia" },
        { value: "om", label: "Oromo" },
        { value: "ps", label: "Pashto" },
        { value: "fa", label: "Persian" },
        { value: "pl", label: "Polish" },
        { value: "pt", label: "Portuguese" },
        { value: "pa", label: "Punjabi" },
        { value: "qu", label: "Quechua" },
        { value: "ro", label: "Romanian" },
        { value: "ru", label: "Russian" },
        { value: "sm", label: "Samoan" },
        { value: "gd", label: "Scottish Gaelic" },
        { value: "sr", label: "Serbian (Cyrillic)" },
        { value: "sr-latn", label: "Serbian (Latin)" },
        { value: "sn", label: "Shona" },
        { value: "zh", label: "Simplified Chinese" },
        { value: "sd", label: "Sindhi" },
        { value: "si", label: "Sinhalese" },
        { value: "sk", label: "Slovak" },
        { value: "sl", label: "Slovenian" },
        { value: "so", label: "Somali" },
        { value: "st", label: "Southern Sotho" },
        { value: "es", label: "Spanish" },
        { value: "su", label: "Sundanese" },
        { value: "sw", label: "Swahili" },
        { value: "sv", label: "Swedish" },
        { value: "tl", label: "Tagalog" },
        { value: "ty", label: "Tahitian" },
        { value: "tg", label: "Tajik" },
        { value: "ta", label: "Tamil" },
        { value: "tt", label: "Tatar" },
        { value: "te", label: "Telugu" },
        { value: "th", label: "Thai" },
        { value: "bo", label: "Tibetan" },
        { value: "ti", label: "Tigrinya" },
        { value: "to", label: "Tongan" },
        { value: "zh-tw", label: "Traditional Chinese" },
        { value: "ts", label: "Tsonga" },
        { value: "tr", label: "Turkish" },
        { value: "tk", label: "Turkmen" },
        { value: "uk", label: "Ukrainian" },
        { value: "ur", label: "Urdu" },
        { value: "ug", label: "Uyghur" },
        { value: "uz", label: "Uzbek" },
        { value: "vi", label: "Vietnamese" },
        { value: "cy", label: "Welsh" },
        { value: "fy", label: "Western Frisian" },
        { value: "xh", label: "Xhosa" },
        { value: "yi", label: "Yiddish" },
        { value: "yo", label: "Yoruba" },
        { value: "zu", label: "Zulu" }
      ],
    enableTranslationAtIterationLevel: 0
  }),

  async mounted() {

    if (this.chosenSubOrg && this.subOrgId) {
      try {
        const res = await api.post("/get-language-preference-suborg", {
          suborg_id: this.subOrgId
        });
        this.selectedLanguage = res.data.default_language || "en";
      } catch (e) {
        console.error("Error fetching language preference:", e.message);
        this.selectedLanguage = "en";
      }
    }

    api
      .get(`sub-organizations/${this.userData.org_id}`)
      .then((response) => {
        this.subOrgList = response.data;
      })
      .catch((e) => {
        flashMessage(
          this.$flashMessage,
          this.brandData.accent_color1
            ? this.brandData.accent_color1
            : "#F47820",
          this.brandData.flash_text_color
            ? this.brandData.flash_text_color
            : "#ffffff",
          e.message
        );
      });
    try {
      const res = await api.get("survey-templates");
      if (res) {
        this.surveyTemplateList = res.data.map((data) => {
          return {
            value: data.survey_template_id,
            label: `${data.survey_template_name} - ${data.survey_description}`,
          };
        });
      }
    } catch (error) {
      flashMessage(
        this.$flashMessage,
        this.brandData.accent_color1 ? this.brandData.accent_color1 : "#F47820",
        this.brandData.flash_text_color
          ? this.brandData.flash_text_color
          : "#ffffff",
        error.message
      );
    }
    try {
      const res = await api.get("survey-templates");
      if (res) {
        this.fullSurveyTemplateList = res.data;
      }
      // console.log(this.fullSurveyTemplateList)
    } catch (error) {
      flashMessage(
        this.$flashMessage,
        this.brandData.accent_color1 ? this.brandData.accent_color1 : "#F47820",
        this.brandData.flash_text_color
          ? this.brandData.flash_text_color
          : "#ffffff",
        error.message
      );
    }
    try {
      const res = await api.get(`/organizations/${this.userData.org_id}`);
      if (res) {
        this.orgName = res.data.org_name;
      }
    } catch (error) {
      console.log(error);
    }
  },
  computed: {
    sortSubOrganizationList() {
      return this.subOrgList.sort((a, b) =>
        a.suborg_name.toUpperCase() < b.suborg_name.toUpperCase()
          ? -1
          : b.suborg_name.toUpperCase() > a.suborg_name.toUpperCase()
          ? 1
          : 0
      );
    },
    sortSurveyTemaplateList() {
      return this.surveyTemplateList.sort((a, b) =>
        a.label.toUpperCase() < b.label.toUpperCase()
          ? -1
          : b.label.toUpperCase() > a.label.toUpperCase()
          ? 1
          : 0
      );
    },
    filteredAvailableLanguages() {
      // Get just the values from selected languages
      const selectedValues = this.selectedLanguages.map(lang => 
        typeof lang === 'object' ? lang.value : lang
      );
      
      // Filter full list to only include selected languages
      return this.availableLanguages.filter(lang => 
        selectedValues.includes(lang.value)
      );
    }
  },
  methods: {

    async loadTranslationSetting() {
      try {
        if (this.chosenSubOrg && this.subOrgId) {
          const res = await api.get(`/sub-organizations-by-suborg/${this.subOrgId}`);
          // Keep as number from database
          this.enableTranslationAtIterationLevel = res.data[0].enable_translation_at_iteration_level ?? 0;
        }
      } catch (error) {
        console.error("Error fetching translation setting:", error.message);
        this.enableTranslationAtIterationLevel = 0;
      }
    },

    saveTranslationSetting() {
      console.log('Translation defined per iteration:', this.enableTranslationAtIterationLevel);
      // todo backend
    },

    async loadSelectedLanguages() {
      if (this.subOrgId) {
        // Check if subOrgId is valid before making API call
        if (!this.subOrgId) {
          console.log("No valid suborg ID provided. Using default languages.");
          this.selectedLanguages = ["en"];
          return;
        }

        try {
          const res = await api.post("/get-selected-languages-suborg", {
            suborg_id: this.subOrgId
          });

          // Start with API values (like ["af", "sq"])
          const langs = res.data.languages || [];

          // Always include "en" if it's not present
          if (!langs.includes("en")) {
            langs.push("en");
          }

          // Assign to selectedLanguages
          this.selectedLanguages = langs;

        } catch (e) {
          console.error("Error fetching selected languages:", e.message);
          this.selectedLanguages = ["en"]; // fallback to just "en"
        }
      }

      // Log only the values (already strings)
      console.log('this.selectedLanguages', this.selectedLanguages);
    }, 

    async saveLanguages() {
      if (!this.subOrgId) {
        flashMessage(
          this.$flashMessage,
          this.brandData.accent_color1 || "#F47820",
          this.brandData.flash_text_color || "#ffffff",
          "Please select a sub-organization first"
        );
        return;
      }

      try {
        // Extract just the language values from selected objects
        const languageValues = this.selectedLanguages.map(lang => 
          typeof lang === 'object' ? lang.value : lang
        );
        
        await api.post("/save-selected-languages-suborg", {
          suborg_id: this.subOrgId,
          languages: languageValues
        });
        
        flashMessage(
          this.$flashMessage,
          this.brandData.accent_color1 || "#F47820",
          this.brandData.flash_text_color || "#ffffff",
          "Available languages saved successfully!"
        );
      } catch (e) {
        flashMessage(
          this.$flashMessage,
          this.brandData.accent_color1 || "#F47820",
          this.brandData.flash_text_color || "#ffffff",
          "Error saving languages: " + e.message
        );
      }
    },

    async loadLanguagePreference() {
      if (this.subOrgId) {
        try {
          const res = await api.post("/get-language-preference-suborg", {
            suborg_id: this.subOrgId
          });
          this.selectedLanguage = res.data.default_language || "en";
        } catch (e) {
          console.error("Error fetching language preference:", e.message);
          this.selectedLanguage = "en";
        }
      }
    },

    saveLanguage() {
      if (!this.subOrgId) {
        flashMessage(
          this.$flashMessage,
          this.brandData.accent_color1 || "#F47820",
          this.brandData.flash_text_color || "#ffffff",
          "Please select a sub-organization first"
        );
        return;
      }

      api
        .post("/save-language-preference-suborg", {
          suborg_id: this.subOrgId,
          language: this.selectedLanguage,
        })
        .then(() => {
          flashMessage(
            this.$flashMessage,
            this.brandData.accent_color1 || "#F47820",
            this.brandData.flash_text_color || "#ffffff",
            "Language preference saved!"
          );
        })
        .catch((e) => {
          flashMessage(
            this.$flashMessage,
            this.brandData.accent_color1 || "#F47820",
            this.brandData.flash_text_color || "#ffffff",
            "Error saving language: " + e.message
          );
        });
    },

    async getSuborg() {
      try {
        const res = await api.get(`sub-organizations/${this.userData.org_id}`);
        if (res) {
          this.subOrgList = res.data;
        }
      } catch (error) {
        flashMessage(
          this.$flashMessage,
          this.brandData.accent_color1
            ? this.brandData.accent_color1
            : "#F47820",
          this.brandData.flash_text_color
            ? this.brandData.flash_text_color
            : "#ffffff",
          error.message
        );
      }
    },
    async getSurveyTemplateAssociationByOrgIdAndSubrogId() {
      try {
        const res = await api.get(
          `survey-template-association/org/${this.userData.org_id}/suborg/${this.subOrgId}`
        );
        if (res) {
          this.chosenSurveyTemplate = res.data.map((data) => {
            return data.survey_template_id;
          });
        }
      } catch (error) {
        flashMessage(
          this.$flashMessage,
          this.brandData.accent_color1
            ? this.brandData.accent_color1
            : "#F47820",
          this.brandData.flash_text_color
            ? this.brandData.flash_text_color
            : "#ffffff",
          error.message
        );
      }
    },
    async addSubOrg() {
      let duplication = false;
      let latestSuborgId = "";
      for (const data of this.subOrgList) {
        if (
          data.suborg_name.toUpperCase() ===
          this.subOrganizationInput.toUpperCase()
        ) {
          flashMessage(
            this.$flashMessage,
            this.brandData.accent_color1
              ? this.brandData.accent_color1
              : "#F47820",
            this.brandData.flash_text_color
              ? this.brandData.flash_text_color
              : "#ffffff",
            this.subOrganizationInput + " already exist!"
          );
          duplication = true;
        }
      }
      if (!duplication) {
        try {
          const res = await api.post("sub-organizations", {
            suborg_name: this.subOrganizationInput.trim(),
            org_id: this.userData.org_id,
            created_by: this.userData.ind_id,
            modified_by: this.userData.ind_id,
          });
          if (res) {
            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color
                ? this.brandData.flash_text_color
                : "#ffffff",
              "Successfully Added sub-organization!"
            );
            this.getSuborg();
          }
        } catch (error) {
          flashMessage(
            this.$flashMessage,
            this.brandData.accent_color1
              ? this.brandData.accent_color1
              : "#F47820",
            this.brandData.flash_text_color
              ? this.brandData.flash_text_color
              : "#ffffff",
            error.message
          );
        }

        try {
          const res = await api.get("sub-organizations/latest");
          if (res) {
            for (const data of res.data) {
              latestSuborgId = data.latest_suborg_id;
            }
          }
        } catch (error) {
          flashMessage(
            this.$flashMessage,
            this.brandData.accent_color1
              ? this.brandData.accent_color1
              : "#F47820",
            this.brandData.flash_text_color
              ? this.brandData.flash_text_color
              : "#ffffff",
            error.message
          );
        }

        try {
          const res = await api.post("brands", {
            brand_name: `${this.orgName}:${this.subOrganizationInput}`,
            font: this.brandData.font,
            font_title_size: this.brandData.font_title_size,
            font_text_size: this.brandData.font_text_size,
            main_color1: this.brandData.main_color1,
            main_color2: this.brandData.main_color2,
            main_color3: this.brandData.main_color3,
            accent_color1: this.brandData.accent_color1,
            website_url: this.brandData.website_url,
            website_sender_email: this.brandData.website_sender_email,
            website_contact_email: this.brandData.website_contact_email,
            website_terms_url: this.brandData.website_terms_url,
            website_privacy_url: this.brandData.website_privacy_url,
            max_search_results: this.brandData.max_search_results,
            flash_text_color: this.brandData.flash_text_color,
            button_text_color: this.brandData.button_text_color,
            header_bg_color: this.brandData.header_bg_color,
            header_text_color: this.brandData.header_text_color,
            header_text_size: this.brandData.header_text_size,
            footer_bg_color: this.brandData.footer_bg_color,
            footer_text_color: this.brandData.footer_text_color,
            footer_text_size: this.brandData.footer_text_size,
            font_title_color: this.brandData.font_title_color,
            font_text_color: this.brandData.font_text_color,
            org_id: this.userData.org_id,
            suborg_id: latestSuborgId,
            created_by: this.userData.ind_id,
            modified_by: this.userData.ind_id,
          });
          if (res) {
            this.getSuborg();
          }
        } catch (error) {
          flashMessage(
            this.$flashMessage,
            this.brandData.accent_color1
              ? this.brandData.accent_color1
              : "#F47820",
            this.brandData.flash_text_color
              ? this.brandData.flash_text_color
              : "#ffffff",
            error.message
          );
        }
      }
      this.addClick++
      this.$emit('addedSuborg', this.addClick)
    },

    selectSubOrg(e) {
      let chosenSubOrg = e.target.value
      for (const data of this.subOrgList) {
        if (data.suborg_name === chosenSubOrg) {
          this.subOrgId = data.suborg_id
          break
        }
      }
      
      // Emit the suborgId and name to parent
      this.$emit('passedSuborgId', this.subOrgId)
      this.$emit('suborgSelected', chosenSubOrg)
      
      this.getSurveyTemplateAssociationByOrgIdAndSubrogId();

      // Wait for reactivity (subOrgId) to be committed before loading languages
      this.$nextTick(() => {
        this.loadSelectedLanguages();
        this.loadLanguagePreference();
        this.loadTranslationSetting();
      });
    },
    selectSurvey(e) {
      console.log(e);
    },
    async updateSubOrg() {
      let duplication = false;
      let noSpacesEnd = this.chosenSubOrg.endsWith(' ')
      let noSpacesStart = this.chosenSubOrg.startsWith(' ')
      for (const data of this.subOrgList) {
        if (
          data.suborg_name.toUpperCase() === this.chosenSubOrg.toUpperCase() &&
          data.suborg_id !== this.subOrgId
        ) {
          flashMessage(
            this.$flashMessage,
            this.brandData.accent_color1
              ? this.brandData.accent_color1
              : "#F47820",
            this.brandData.flash_text_color
              ? this.brandData.flash_text_color
              : "#ffffff",
            this.chosenSubOrg + " already exist!"
          );
          duplication = true;
        }
      }
      if (!duplication && this.chosenSubOrg.length) {
        if(noSpacesEnd === false && noSpacesStart === false){
          try {
            const res = await api.put(`sub-organizations/${this.subOrgId}`, {
              suborg_name: this.chosenSubOrg.trim(),
              modified_by: this.userData.ind_id,
            });
            if (res) {
              flashMessage(
                this.$flashMessage,
                this.brandData.accent_color1
                  ? this.brandData.accent_color1
                  : "#F47820",
                this.brandData.flash_text_color
                  ? this.brandData.flash_text_color
                  : "#ffffff",
                "Successfully updated sub-organization!"
              );
              this.getSuborg();
            }
          } catch (error) {
            flashMessage(
              this.$flashMessage,
              this.brandData.accent_color1
                ? this.brandData.accent_color1
                : "#F47820",
              this.brandData.flash_text_color
                ? this.brandData.flash_text_color
                : "#ffffff",
              error.message
            );
          }
        } else {
          flashMessage(
            this.$flashMessage,
            this.brandData.accent_color1
              ? this.brandData.accent_color1
              : "#F47820",
            this.brandData.flash_text_color
              ? this.brandData.flash_text_color
              : "#ffffff",
            "Please remove extra spaces in the beginning or end of the sub-organization name"
          );
        }

        try {
          const res = await api.delete(
            `survey-template-association/org/${this.userData.org_id}/suborg/${this.subOrgId}`
          );
          if (res) {
            const templateValues = Object.values(this.chosenSurveyTemplate);
            templateValues.forEach((data) => {
              for (const res of this.fullSurveyTemplateList) {
                if (res.survey_template_id === data) {
                  this.surveyFilenameId = res.survey_file;
                }
              }
              // console.log(this.surveyFilenameId)
              // api
              //   .get(
              //     `survey-template-association/org/${this.userData.org_id}/survey-template/${data}/suborg/0`
              //   )
              //   .then((res) => {
              //     this.surveyURL = res.data.map((data) => {
              //       return data.survey_url
              //     })
              try {
                const res = api.post("Survey-Template-Association", {
                  survey_template_association_name: "",
                  survey_template_id: data,
                  survey_template_link: `https://mnetdemo.gcm3.com/${this.surveyFilenameId}`,
                  org_id: this.userData.org_id,
                  suborg_id: this.subOrgId,
                  created_by: this.userData.ind_id,
                  modified_by: this.userData.ind_id,
                });
                if (res) {
                  this.getSurveyTemplateAssociationByOrgIdAndSubrogId();
                }
              } catch (error) {
                flashMessage(
                  this.$flashMessage,
                  this.brandData.accent_color1
                    ? this.brandData.accent_color1
                    : "#F47820",
                  this.brandData.flash_text_color
                    ? this.brandData.flash_text_color
                    : "#ffffff",
                  error.message
                );
              }
              // })
              // .catch((error) => {
              //   this.$flashMessage.show({
              //     type: 'error',
              //     title: error.message,
              //     message: '',
              //   })
              // })
            });
          }
        } catch (error) {
          flashMessage(
            this.$flashMessage,
            this.brandData.accent_color1
              ? this.brandData.accent_color1
              : "#F47820",
            this.brandData.flash_text_color
              ? this.brandData.flash_text_color
              : "#ffffff",
            error.message
          );
        }
      }
      this.addClick++
      this.$emit('updateSuborg', this.addClick)
    },
    clearAddField() {
      this.subOrganizationInput = "";
    },
    clearUpdatedFields() {
      this.subOrgId = "";
      this.chosenSubOrg = "";
      this.chosenSurveyTemplate = [];
    },
  },
};
</script>
<style src="@vueform/multiselect/themes/default.css"></style>
<style scoped>
.subOrganizationBox {
  padding-bottom: 20px;
  margin-bottom: 20px;
  font-size: 14px;
  min-width: 320px; /* Minimum width to prevent extreme compression */
}

.formBox {
  display: flex;
  flex-direction: column; /* Stack elements vertically on small screens */
  width: 100%;
}

.formControl {
  font-weight: 400;
  line-height: 1.5;
  color: #212529;
  background-color: #fff;
  background-clip: padding-box;
  border-width: 0 0 1px;
  border-color: grey;
  padding: 2px 5px;
  margin-bottom: 1em;
  outline: 0;
  width: 100%; /* More standard than -webkit-fill-available */
  max-width: 100%; /* Ensure it doesn't overflow */
}

input, select, option, textarea {
  border: 1px solid grey;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.buttonBox {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

@media (max-width: 768px) {
  /* Additional mobile-specific styles */
  .buttonBox {
    flex-direction: column;
  }
  
  .subOrganizationBox {
    padding-bottom: 20px;
    margin-bottom: 30px;
  }
}

.profile-header {
  margin-bottom: 10px;
  font-size: 16px;
  font-weight: 600;
}

.main-label {
  font-weight: 500;
  margin-bottom: 8px;
  display: block;
}

.language-select {
  width: 100%;
  padding: 12px;
  margin-top: 8px;
  border: 1px solid grey;
  border-radius: 4px;
  max-width: 30%;
  font-size: 14px;
}

.formBox hr {
  margin: 10px 0;
  border: none;
  border-top: 1px solid #ddd;
}

.multiselect-tags {
  border: 1px solid grey;
  border-radius: 4px;
  min-height: 38px;
}

.multiselect-tag {
  background: #f0f0f0;
}

@media (max-width: 768px) {
  .language-select {
    max-width: 100%;
  }
}

.tooltip {
  position: relative;
  display: inline-block;
  cursor: pointer;
  color: #212529;
  margin-bottom: 0.5rem;
}

.tooltip .tooltiptext {
  visibility: hidden;
  width: 200px;
  background-color: black;
  color: #fff;
  text-align: center;
  border-radius: 6px;
  padding: 5px 0;
  position: absolute;
  z-index: 1;
  top: 125%; /* adjust as needed */
  left: 50%;
  transform: translateX(-50%);
}

.tooltip:hover .tooltiptext {
  visibility: visible;
}
</style>
